// storeService.ts
import apiService, { API_URL } from "../apiUtils"
import axiosInstance from "../../CustomAxios/axiosInstance"

// Define the Store interface based on your database schema
export interface Store {
    id?: number
    store_name: string
    slogan: string
    email: string
    phone: string
    address: string
    logo: string | null
    primary_color: string
    secondary_color: string
    vat_rate: number
    loyalty_point_value: number
    card_deduction: number
    admin_password?: string
    ice: string
}

// Define the StoreHeader interface that matches the API response
export interface StoreHeader {
    store_name: string
    slogan: string
    email: string
    phone: string
    address: string
    logo: string | null
    primary_color: string
    secondary_color: string
    loyalty_point_value: number
    admin_password: string
    card_deduction: number
    vat_rate?: number // Added vat_rate as an optional field
}

// Default store data to use as fallback
const defaultStoreData: Store = {
    store_name: "Default Store",
    slogan: "Your slogan here",
    email: "store@example.com",
    phone: "0600000000",
    address: "Store Address",
    logo: null,
    primary_color: "#10b981",
    secondary_color: "#ffffff",
    vat_rate: 20,
    loyalty_point_value: 0.5,
    card_deduction: 0,
    ice: ""
}

// Default store header data
const defaultStoreHeader: StoreHeader = {
    store_name: "Default Store",
    slogan: "Your slogan here",
    email: "store@example.com",
    phone: "0600000000",
    address: "Store Address",
    logo: null,
    primary_color: "#10b981",
    secondary_color: "#ffffff",
    loyalty_point_value: 0.5,
    admin_password: "",
    card_deduction: 0,
    vat_rate: 20, // Added default vat_rate
}

// In-memory cache
let storeDataCache: Store | null = null
let storeHeaderCache: StoreHeader | null = null
let vatRateCache: number | null = null // Add a separate cache for VAT rate

// Cache timestamps and duration
let storeHeaderCacheTime = 0
let storeDataCacheTime = 0
let vatRateCacheTime = 0
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes in milliseconds

const storeService = {
    /**
     * Clear the cache to force fresh data fetch
     */
    clearCache(): void {
        storeDataCache = null
        storeHeaderCache = null
        vatRateCache = null // Clear VAT rate cache
        storeHeaderCacheTime = 0
        storeDataCacheTime = 0
        vatRateCacheTime = 0
        console.log("Store service cache cleared")

        // Also clear API cache for store-related endpoints
        if (apiService.clearCache) {
            apiService.clearCache()
        }
    },

    /**
     * Get VAT rate from the API
     * This is a separate method to handle the case where VAT rate might not be in the header
     */
    async getVatRate(): Promise<number> {
        try {
            const now = Date.now()
            // Return cached VAT rate if available and not expired
            if (vatRateCache !== null && now - vatRateCacheTime < CACHE_DURATION) {
                console.log("Using cached VAT rate")
                return vatRateCache
            }

            // Try to get from params endpoint which should have the VAT rate
            const response = await apiService.get("/store/params")

            if (response.status === "success" && response.data) {
                const vatRate = Number.parseFloat(response.data.vat_rate) || defaultStoreData.vat_rate

                // Cache the VAT rate with timestamp
                vatRateCache = vatRate
                vatRateCacheTime = now

                return vatRate
            }

            // If params endpoint fails, return default VAT rate
            return defaultStoreData.vat_rate
        } catch (error) {
            console.error("Error fetching VAT rate:", error)
            return defaultStoreData.vat_rate
        }
    },

    /**
     * Get store data from the API - fetches the single store row
     * Note: This method may fail with 403 Forbidden for cashier role
     */
    async getStoreData(): Promise<Store> {
        try {
            const now = Date.now()
            // Return cached data if available and not expired
            if (storeDataCache && now - storeDataCacheTime < CACHE_DURATION) {
                console.log("Using cached store data")
                return storeDataCache
            }

            // For admin pages like settings, prioritize the /store/params endpoint
            // which contains all store data including VAT rate
            try {
                const response = await apiService.get("/store/params")

                if (response.status === "success" && response.data) {
                    const storeData = response.data
                    console.log("Store Params: ", storeData)
                    // Transform API response to match Store interface format
                    const transformedData: Store = {
                        id: storeData.id,
                        store_name: storeData.store_name || "",
                        slogan: storeData.slogan || "",
                        email: storeData.email || "",
                        phone: storeData.phone || "",
                        address: storeData.address || "",
                        logo: storeData.logo ? `${API_URL}/storage/${storeData.logo}` : null,
                        primary_color: storeData.primary_color || "#10b981",
                        secondary_color: storeData.secondary_color || "#ffffff",
                        vat_rate: Number.parseFloat(storeData.vat_rate) || defaultStoreData.vat_rate,
                        loyalty_point_value: Number.parseFloat(storeData.loyalty_point_value) || 0,
                        card_deduction: Number.parseFloat(storeData.card_deduction || "0") || 0,
                        admin_password: storeData.admin_password,
                        ice: storeData.ice || ""
                    }

                    // Cache the data with timestamp
                    storeDataCache = transformedData
                    storeDataCacheTime = now

                    // Also cache the VAT rate
                    vatRateCache = transformedData.vat_rate
                    vatRateCacheTime = now

                    console.log("Loaded store data from /store/params with VAT rate:", transformedData.vat_rate)
                    return transformedData
                }
            } catch (paramsError) {
                console.log("Could not get data from params endpoint, falling back to header endpoint", paramsError)
            }

            // Fall back to store header if params endpoint fails (shouldn't happen for admin users)
            try {
                const headerData = await this.getStoreHeader()

                // Get VAT rate separately if not in header
                const vatRate = headerData.vat_rate !== undefined ? headerData.vat_rate : await this.getVatRate()

                // Convert StoreHeader to Store
                const transformedData: Store = {
                    store_name: headerData.store_name,
                    slogan: headerData.slogan,
                    email: headerData.email,
                    phone: headerData.phone,
                    address: headerData.address,
                    logo: headerData.logo,
                    primary_color: headerData.primary_color,
                    secondary_color: headerData.secondary_color,
                    vat_rate: vatRate,
                    loyalty_point_value: headerData.loyalty_point_value,
                    card_deduction: headerData.card_deduction,
                    admin_password: headerData.admin_password,
                    ice: ""
                }

                // Cache the data with timestamp
                storeDataCache = transformedData
                storeDataCacheTime = now

                console.log("Loaded store data from header with VAT rate:", transformedData.vat_rate)
                return transformedData
            } catch (headerError) {
                console.error("Could not get data from header endpoint either", headerError)
            }

            throw new Error("Failed to load store data from any endpoint")
        } catch (error) {
            console.error("Error fetching store data:", error)

            // Return default data during development
            console.log("Using default store data:", defaultStoreData)
            return defaultStoreData
        }
    },

    /**
     * Get store header data - comprehensive store information
     * @returns Promise that resolves to the store header data
     */
    async getStoreHeader(): Promise<StoreHeader> {
        try {
            const now = Date.now()
            // Return cached data if available and not expired
            if (storeHeaderCache && now - storeHeaderCacheTime < CACHE_DURATION) {
                console.log("Using cached store header data")
                return storeHeaderCache
            }

            // Make API call to get store header data
            const response = await apiService.get("/store/store_header")

            if (response.status === "success" && response.data) {
                const headerData = response.data

                console.log("Store Header: ", headerData)

                // Transform API response to match StoreHeader interface
                const transformedHeader: StoreHeader = {
                    store_name: headerData.store_name || defaultStoreHeader.store_name,
                    slogan: headerData.slogan || defaultStoreHeader.slogan,
                    email: headerData.email || defaultStoreHeader.email,
                    phone: headerData.phone || defaultStoreHeader.phone,
                    address: headerData.address || defaultStoreHeader.address,
                    logo: headerData.logo ? `${API_URL}/storage/${headerData.logo}` : null,
                    primary_color: headerData.primary_color || defaultStoreHeader.primary_color,
                    secondary_color: headerData.secondary_color || defaultStoreHeader.secondary_color,
                    loyalty_point_value:
                        Number.parseFloat(headerData.loyalty_point_value) || defaultStoreHeader.loyalty_point_value,
                    admin_password: headerData.admin_password || defaultStoreHeader.admin_password,
                    card_deduction: Number.parseFloat(headerData.card_deduction) || defaultStoreHeader.card_deduction,
                    // Include VAT rate if it exists in the header data
                    ...(headerData.vat_rate !== undefined && { vat_rate: Number.parseFloat(headerData.vat_rate) }),
                }

                // Cache the header data with timestamp
                storeHeaderCache = transformedHeader
                storeHeaderCacheTime = now

                return transformedHeader
            }

            throw new Error("Failed to load store header data")
        } catch (error) {
            console.error("Error fetching store header data:", error)

            // Return default header data as last resort
            console.log("Using default store header data:", defaultStoreHeader)
            return defaultStoreHeader
        }
    },

    /**
     * Update store data - updates the single store row
     * @param storeData The store data to update
     */
    async updateStoreData(storeData: FormData): Promise<any> {
        try {
            // Debugging: Log the formData keys and values
            console.log("Updating store with data (keys only):", Array.from(storeData.keys()))

            // Make the API call using our apiService with proper content type for FormData
            const response = await apiService.post("/store/params", storeData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Accept: "application/json",
                },
            })

            if (response.status === "success") {
                // Clear the cache after a successful update
                this.clearCache()
                return response
            }

            throw new Error(response.message || "Failed to update store data")
        } catch (error) {
            console.error("Error updating store data:", error)
            throw error
        }
    },

    /**
     * Get admin password
     * @returns Promise that resolves to the admin password
     */
    async getAdminPassword(): Promise<string | null> {
        try {
            // Get the header data which includes admin_password
            const headerData = await this.getStoreHeader()
            return headerData.admin_password || null
        } catch (error) {
            console.error("Error getting admin password:", error)
            return null
        }
    },

    /**
     * Get card deduction rate percentage
     * @returns Promise that resolves to the card deduction rate (as a decimal)
     */
    async getCardDeduction(): Promise<number> {
        try {
            // Get the header data which includes card_deduction
            const headerData = await this.getStoreHeader()
            // Convert to decimal (e.g., 2.5 -> 0.025)
            return headerData.card_deduction / 100
        } catch (error) {
            console.error("Error getting card deduction rate:", error)
            // Return default value of 2.5%
            return 0.025
        }
    },

    /**
     * Verify if a provided password matches the admin password
     * @param password The password to verify
     * @returns Promise resolving to true if password matches
     */
    async verifyPassword(password: string): Promise<any> {
     

            // Get admin password from getStoreHeader
            const adminPassword = await axiosInstance.post("/store/verify-admin-password",{admin_password: password})

         
    },
}

export default storeService

